import { useCallback, useEffect, useState } from "react";
import { ChoreGrid, type CellState, type SetState } from "../components/ChoreGrid";
import { Leaf, Line, Section, Stub } from "../components/Passbook";
import {
  CapabilityList,
  PresetPicker,
  SpecialPolicyPicker,
  usePresets,
} from "../components/Capabilities";
import {
  api,
  ApiError,
  type Bucket,
  type Chart,
  type Child,
  type Goal,
  type HistoryRow,
  type ChoreInstance,
} from "../lib/api";
import { amountReading, formatBp, formatCents } from "../lib/money";
import { useLiveData } from "../lib/useLiveData";
import { Field } from "./Setup";

type Statement = {
  yearMonth: string;
  rows: HistoryRow[];
  moneyInCents: number;
  moneyOutCents: number;
  netCents: number;
  count: number;
};

type SpendingSummary = {
  byCategory: { category: string; amountCents: number }[];
  totalCents: number;
};

type Period = {
  id: string;
  periodKey: string;
  periodLabel: string;
  status: string;
  baseAmountCents: number;
  computedAmountCents: number | null;
  earnedBp: number | null;
  isCatchup: boolean;
  reviewReason: "no_records" | "chores_incomplete" | null;
  reviewDecision: "full" | "partial" | "none" | null;
  choresDone: number | null;
  choresRequired: number | null;
  payoutAt: string;
  isPayable: boolean;
};

type Schedule = {
  frequency: string;
  amountCents: number;
  payoutTimeLocal: string;
  weekStartDay: number;
  dayOfMonth: number;
  minimumCompletionBp: number | null;
};

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday",
                  "Saturday", "Sunday"];

const FREQUENCIES = [
  { value: "daily", label: "Every day" },
  { value: "weekly", label: "Every week" },
  { value: "biweekly", label: "Every other week" },
  { value: "monthly", label: "Every month" },
];

// What the engine actually did with a period, in a parent's words.
const PERIOD_STATUS: Record<string, string> = {
  open: "Still running",
  closed: "Counting up",
  evaluating: "Counting up",
  paid: "Paid",
  needs_review: "Waiting for you",
  skipped: "Skipped",
  failed: "Didn't go through",
};

// What a grown-up said about a week that fell short, once they had said it.
const DECIDED: Record<string, string> = {
  full: "you said the jobs were done",
  partial: "you set the amount",
  none: "you said it wasn't earned",
};

type SplitPart = {
  bucketId: string;
  key: string;
  name: string;
  color: string | null;
  amountCents: number;
  percentBp?: number;
};

export function ChildAdmin({ childId, onArchived }: { childId: string; onArchived: () => void }) {
  const catalogue = usePresets();
  const [child, setChild] = useState<Child | null>(null);
  const [buckets, setBuckets] = useState<Bucket[]>([]);
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [goal, setGoal] = useState<Goal | null>(null);
  const [preview, setPreview] = useState<SplitPart[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  /* The schedule boxes, the jar percentages and the count boxes are all
     seeded from load(). A background refresh landing mid-edit would quietly
     retype them, so while any of them has been touched the page holds still
     and only refreshes again once the change is saved. */
  const [dirty, setDirty] = useState(false);

  const [amount, setAmount] = useState("5.00");
  const [threshold, setThreshold] = useState("80");
  const [frequency, setFrequency] = useState("weekly");
  const [payDay, setPayDay] = useState(0);          // 0=Mon .. 6=Sun
  const [dayOfMonth, setDayOfMonth] = useState(1);  // 31 means "the last day"
  const [payTime, setPayTime] = useState("07:00");
  const [startFrom, setStartFrom] = useState("");
  const [catchUpAmounts, setCatchUpAmounts] = useState<Record<string, string>>({});
  const [percents, setPercents] = useState<Record<string, string>>({});
  const [chart, setChart] = useState<Chart | null>(null);
  const [counts, setCounts] = useState<Record<string, string>>({});
  const [countMemo, setCountMemo] = useState("");
  const [cellBusy, setCellBusy] = useState<string | null>(null);
  const [adjustJar, setAdjustJar] = useState("");
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustMemo, setAdjustMemo] = useState("");
  const [goalName, setGoalName] = useState("");
  const [goalTarget, setGoalTarget] = useState("");
  const [fromJar, setFromJar] = useState("");
  const [toJar, setToJar] = useState("");
  const [moveAmount, setMoveAmount] = useState("");
  const [pendingMove, setPendingMove] = useState<
    { fromId: string; toId: string; cents: number } | null
  >(null);
  const [loginName, setLoginName] = useState("");
  const [pin, setPin] = useState("");
  const [editingLogin, setEditingLogin] = useState(false);
  const [statement, setStatement] = useState<Statement | null>(null);
  const [spending, setSpending] = useState<SpendingSummary | null>(null);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));

  const load = useCallback(async () => {
    try {
      const [c, b, s, h, g, pr] = await Promise.all([
        api.get<Child>(`/children/${childId}`),
        api.get<Bucket[]>(`/children/${childId}/buckets`),
        api.get<{ schedule: Schedule | null }>(`/children/${childId}/schedule`),
        api.get<{ rows: HistoryRow[] }>(`/children/${childId}/history`),
        api.get<{ goals: Goal[] }>(`/children/${childId}/goals`),
        api.get<{ periods: Period[] }>(`/children/${childId}/periods`),
      ]);
      setChild(c);
      setBuckets(b);
      setSchedule(s.schedule);
      // Seed the count boxes with what the app currently believes. A parent
      // auditing four jars usually corrects one of them, and retyping the
      // three that were already right is how a typo gets into the ledger.
      setCounts(Object.fromEntries(
        b.map((x) => [x.id, (x.balanceCents / 100).toFixed(2)])));
      // A missing schedule is normal before one is set up; the jobs grid
      // simply does not appear.
      try {
        setChart(await api.get<Chart>(`/children/${childId}/chart`));
      } catch {
        setChart(null);
      }
      setPeriods(pr.periods);
      setHistory(h.rows);
      setGoal(g.goals.find((x) => x.status === "active") ?? null);
      setPercents(
        Object.fromEntries(b.map((x) => [x.id, String((x.percentBp ?? 0) / 100)])),
      );
      if (!adjustJar && b.length) setAdjustJar(b[0].id);
      if (!fromJar && b.length) setFromJar(b[0].id);
      if (!toJar && b.length > 1) setToJar(b[1].id);
      if (s.schedule) {
        setAmount((s.schedule.amountCents / 100).toFixed(2));
        setFrequency(s.schedule.frequency);
        setPayDay(s.schedule.weekStartDay ?? 0);
        setDayOfMonth(s.schedule.dayOfMonth ?? 1);
        setPayTime(s.schedule.payoutTimeLocal ?? "07:00");
        if (s.schedule.minimumCompletionBp != null) {
          setThreshold(String(s.schedule.minimumCompletionBp / 100));
        }
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId]);

  useLiveData(load, { paused: dirty });

  useEffect(() => {
    api
      .get<Statement>(`/children/${childId}/statement/${month}`)
      .then(setStatement)
      .catch(() => setStatement(null));
    api
      .get<SpendingSummary>(`/children/${childId}/spending-summary`)
      .then(setSpending)
      .catch(() => setSpending(null));
  }, [childId, month, history]);

  // The preview always comes from the server's real split(). Reimplementing
  // largest-remainder apportionment here would guarantee the number a parent
  // sees and the number that posts eventually disagree by a penny.
  useEffect(() => {
    const cents = Math.round(parseFloat(amount || "0") * 100);
    if (!Number.isFinite(cents) || cents < 0) return;
    api
      .post<{ parts: SplitPart[] }>(`/children/${childId}/split-preview`, {
        amountCents: cents,
      })
      .then((r) => setPreview(r.parts))
      .catch(() => setPreview([]));
  }, [amount, childId, buckets]);

  async function run(key: string, fn: () => Promise<unknown>, message?: string) {
    setBusy(key);
    setError(null);
    setNote(null);
    try {
      await fn();
      await load();
      setDirty(false);
      if (message) setNote(message);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "That didn't work.");
    } finally {
      setBusy(null);
    }
  }

  /* A counted amount, in cents, or null if it is not a usable number.
     Blank is null rather than zero: "I have not typed this yet" and "this jar
     is empty" are different answers and only one of them is safe to post. */
  const countCents = (raw: string | undefined): number | null => {
    if (raw === undefined || raw.trim() === "") return null;
    const cents = Math.round(parseFloat(raw) * 100);
    return Number.isFinite(cents) && cents >= 0 ? cents : null;
  };
  const countTotalDelta = buckets.reduce<number | null>((sum, b) => {
    const cents = countCents(counts[b.id]);
    return sum === null || cents === null ? null : sum + (cents - b.balanceCents);
  }, 0);
  const countSummary =
    countTotalDelta === null || countTotalDelta === 0
      ? ""
      : `${countTotalDelta > 0 ? "+" : "\u2212"}${formatCents(Math.abs(countTotalDelta))}`;

  /* Setting a job from the grid. Reloading the whole child after every tap
     would be a round trip per cell on a page that already fetches six things,
     so the cell is patched in place and the chart re-read once. */
  async function setCell(instance: ChoreInstance, next: SetState) {
    setCellBusy(instance.id);
    setError(null);
    setNote(null);
    try {
      const r = await api.post<{ periodAlreadyPaid: boolean }>(
        `/chore-instances/${instance.id}/set-state`, { state: next });
      setChart(await api.get<Chart>(`/children/${childId}/chart`));
      if (r.periodAlreadyPaid) {
        setNote("Changed \u2014 but that week was already paid, so the money did not move.");
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "That didn't work.");
    } finally {
      setCellBusy(null);
    }
  }

  const percentTotal = Object.values(percents).reduce(
    (sum, v) => sum + (parseFloat(v) || 0),
    0,
  );
  const percentsValid = Math.abs(percentTotal - 100) < 0.005;

  // Payout lands on the date the period ENDS, which for a weekly schedule is
  // the same weekday it starts on. Saying so plainly beats making a parent
  // work it out from a label called "week start day".
  const payTimeLabel = formatTime(payTime);
  const scheduleHint =
    frequency === "daily"
      ? `Paid every morning at ${payTimeLabel}, for the day before.`
      : frequency === "monthly"
        ? `Paid on ${ordinal(dayOfMonth)} at ${payTimeLabel}, for the month before.`
        : `Paid ${WEEKDAYS[payDay]} at ${payTimeLabel}, for the ${
            frequency === "biweekly" ? "two weeks" : "week"
          } before.`;

  const unpaid = periods.filter((p) => p.isPayable);
  const special = buckets.find((b) => b.kind === "special") ?? null;
  const lastPaid = periods.find((p) => p.status === "paid");

  /* The stamp answers the question a parent opens this page with. Owing beats
     paid beats nothing: if three weeks are unpaid, that a fourth was paid in
     July is not the headline. */
  const stamp = !schedule
    ? { label: "No allowance", sub: "not set", tone: "none" as const }
    : unpaid.length > 0
      ? {
          label: unpaid.length === 1 ? "1 payday" : `${unpaid.length} paydays`,
          sub: "unpaid",
          tone: "owing" as const,
        }
      : lastPaid
        ? { label: "Paid", sub: lastPaid.periodLabel, tone: "paid" as const }
        : { label: "No paydays", sub: "yet", tone: "none" as const };

  const capsOn = catalogue
    ? catalogue.capabilities.filter(
        (c) => c.fromAge < 900 && child?.capabilities[c.key],
      ).length
    : 0;
  const capsTotal = catalogue
    ? catalogue.capabilities.filter((c) => c.fromAge < 900).length
    : 0;
  const splitSummary = buckets
    .map((b) => String(Math.round((b.percentBp ?? 0) / 100)))
    .join("/");
  const policyLabel =
    catalogue?.specialPolicies.find((p) => p.key === child?.specialPolicy)?.label ?? "";

  if (!child) return <p>{error ?? "Loading…"}</p>;

  return (
    <div>
      <Stub
        child={child}
        buckets={buckets}
        scheduleLine={schedule ? scheduleHint : "No allowance set yet."}
        stamp={stamp}
      />

      {/* MONEY -- the things a parent came here to DO. Open by default when
           something is waiting; there is no point hiding the one row that
           needs them. */}
      <Section label="Money" />
      <Leaf>
        <Line
          label="This week&rsquo;s jobs"
          value={chart ? `${chart.todayDone}/${chart.todayTotal} today` : ""}
          defaultOpen
        >
          {chart === null || chart.instances.length === 0 ? (
            <p style={{ margin: 0, fontSize: 13.5, color: "var(--ink-2)" }}>
              No jobs this week. Add them under Jobs on the grown-ups&rsquo;
              home screen.
            </p>
          ) : (
            <>
              <p style={{ margin: "0 0 14px", fontSize: 13.5, color: "var(--ink-2)" }}>
                Tap any square to say what happened &mdash; done, not done, or
                excused. Works for days already gone: a child can only check a
                job off while it is fresh, but a grown-up can put the week
                right whenever they get to it.
              </p>
              <ChoreGrid
                instances={chart.instances}
                days={chart.days}
                todayLocalDate={chart.todayLocalDate}
                onSet={setCell}
                busyId={cellBusy}
                nameWidth={104}
              />
              <div style={{ display: "flex", flexWrap: "wrap", gap: 14,
                            marginTop: 14, fontSize: 12, color: "var(--ink-3)" }}>
                <Key look="done" label="Done" />
                <Key look="claimed" label="Says done, not checked" />
                <Key look="missed" label="Missed" />
                <Key look="excused" label="Excused, does not count against them" />
              </div>
            </>
          )}
        </Line>

        <Line
          label="Paydays"
          value={unpaid.length > 0 ? `${unpaid.length} unpaid` : `${periods.length} so far`}
          alert={unpaid.length > 0}
          defaultOpen={unpaid.length > 0}
        >
          <div style={{ marginBottom: 12 }}>
            <button
              disabled={busy === "catchup"}
              onClick={() =>
                run("catchup", async () => {
                  const r = await api.post<{
                    paid: number; queuedForReview: number; materialized: number;
                  }>(`/children/${childId}/catch-up`);
                  setNote(
                    r.paid + r.queuedForReview === 0
                      ? "Nothing was missed."
                      : `Found ${r.paid + r.queuedForReview}. ` +
                        (r.queuedForReview > 0
                          ? "Approve them below."
                          : "Paid."),
                  );
                })
              }
              style={{ minHeight: 44, fontSize: 14, padding: "0 14px" }}
            >
              {busy === "catchup" ? "Checking…" : "Check for missed ones"}
            </button>
          </div>
          <p style={{ margin: "0 0 12px", fontSize: 13.5, color: "var(--ink-2)" }}>
            {unpaid.length > 0
              ? `${unpaid.length} never got paid. Each one is its own payment, so ${child.displayName}'s history still reads one line per payday.`
              : "This is what was paid, and when."}
          </p>

          {periods.length === 0 ? (
            <p style={{ color: "var(--ink-2)", margin: 0 }}>
              No paydays yet. Set an allowance above and the first one appears here.
            </p>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {periods.map((p) => (
                <div
                  key={p.id}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                    padding: "9px 0", borderBottom: "1px solid var(--paper-edge)",
                  }}
                >
                  <span className="money" style={{ fontSize: 13, minWidth: 92 }}>
                    {p.periodLabel}
                  </span>
                  <span style={{ flex: 1, fontSize: 14, color: "var(--ink-2)" }}>
                    {PERIOD_STATUS[p.status] ?? p.status}
                    {p.isCatchup && " · catch-up"}
                    {/* A week waiting on a grown-up is waiting for a REASON,
                        and the count is the reason. "Waiting for you" alone
                        sends a parent hunting through the chart for what it
                        was they were supposed to look at. */}
                    {p.status === "needs_review" &&
                      p.reviewReason === "chores_incomplete" &&
                      p.choresRequired != null &&
                      ` · only ${p.choresDone} of ${p.choresRequired} jobs`}
                    {p.status === "needs_review" &&
                      p.reviewReason !== "chores_incomplete" &&
                      " · no jobs were recorded"}
                    {p.reviewDecision != null && ` · ${DECIDED[p.reviewDecision]}`}
                    {p.earnedBp != null && p.status === "paid" &&
                      ` · ${formatBp(p.earnedBp)} of jobs`}
                  </span>
                  {p.isPayable ? (
                    <>
                      <input
                        inputMode="decimal"
                        aria-label={`Amount for ${p.periodLabel}`}
                        value={
                          catchUpAmounts[p.id] ??
                          ((p.computedAmountCents ?? p.baseAmountCents) / 100).toFixed(2)
                        }
                        onChange={(e) =>
                          setCatchUpAmounts({ ...catchUpAmounts, [p.id]: e.target.value })
                        }
                        className="money"
                        style={{ width: 88, minHeight: 44, textAlign: "right" }}
                      />
                      <button
                        disabled={busy === p.id}
                        onClick={() =>
                          run(
                            p.id,
                            () =>
                              api.post(`/periods/${p.id}/approve`, {
                                amountCents: Math.round(
                                  parseFloat(
                                    catchUpAmounts[p.id] ??
                                      String(
                                        (p.computedAmountCents ?? p.baseAmountCents) / 100,
                                      ),
                                  ) * 100,
                                ),
                              }),
                            "Paid.",
                          )
                        }
                        style={{ minHeight: 44, fontSize: 13, padding: "0 14px" }}
                      >
                        Pay it
                      </button>
                      {/* The box above answers "what is it worth"; these two
                          answer the question that comes first. "They were
                          done" ticks the week's jobs off as well as paying it,
                          so the grid on this same page stops contradicting the
                          payment. */}
                      {p.reviewReason === "chores_incomplete" && (
                        <>
                          <button
                            disabled={busy === p.id}
                            title="Ticks off that week's jobs too, so the chart matches"
                            onClick={() =>
                              run(p.id, () =>
                                api.post(`/periods/${p.id}/decide`,
                                         { decision: "full" }),
                                "Paid in full, and the jobs are ticked off.")
                            }
                            style={{ minHeight: 44, fontSize: 13, padding: "0 14px" }}
                          >
                            They were done
                          </button>
                          <button
                            disabled={busy === p.id}
                            title="Goes on their statement as a $0.00 payday"
                            onClick={() =>
                              run(p.id, () =>
                                api.post(`/periods/${p.id}/decide`,
                                         { decision: "none" }),
                                "Recorded as not earned.")
                            }
                            style={{ minHeight: 44, fontSize: 13, padding: "0 14px" }}
                          >
                            Not earned
                          </button>
                        </>
                      )}
                    </>
                  ) : (
                    <span className="money" style={{ fontWeight: 700 }}>
                      {p.computedAmountCents != null
                        ? formatCents(p.computedAmountCents)
                        : "—"}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </Line>

        <Line label="Count the jars" value={countSummary}>
          <p style={{ margin: "0 0 12px", fontSize: 13.5, color: "var(--ink-2)" }}>
            What each jar <em>actually</em> holds. Use this to set the amounts
            a child already had before the app, and to square things up later
            when the app and the jars have drifted. Type the number you
            counted &mdash; the difference is worked out for you.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const payload = buckets
                .map((b) => ({ bucketId: b.id, amountCents: countCents(counts[b.id]) }))
                .filter((c) => c.amountCents !== null) as
                  { bucketId: string; amountCents: number }[];
              if (payload.length !== buckets.length) {
                setError("Every jar needs an amount, and none can be negative.");
                return;
              }
              void run(
                "count",
                async () => {
                  const r = await api.put<{ changed: unknown[] }>(
                    `/children/${childId}/jar-balances`,
                    { counts: payload, memo: countMemo },
                  );
                  setCountMemo("");
                  setNote(
                    r.changed.length === 0
                      ? "Already correct \u2014 nothing to change."
                      : `Set ${r.changed.length} jar${r.changed.length === 1 ? "" : "s"}.`,
                  );
                },
              );
            }}
            style={{ display: "grid", gap: 12 }}
          >
            <div style={{ display: "grid", gap: 10 }}>
              {buckets.map((b) => {
                const cents = countCents(counts[b.id]);
                const delta = cents === null ? null : cents - b.balanceCents;
                return (
                  <div key={b.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span aria-hidden="true" style={{
                      width: 12, height: 12, borderRadius: 3,
                      background: b.color ?? "var(--ink-3)", flex: "0 0 auto" }} />
                    <span style={{ flex: 1, minWidth: 0 }}>{b.name}</span>
                    {/* The change, stated before it is made. A count is the one
                        money action where the parent knows the destination but
                        not the movement, and an unexpected number here is the
                        last chance to catch a mistyped jar. */}
                    <span className="money" style={{
                      fontSize: 12.5, width: 74, textAlign: "right",
                      color: delta === null || delta === 0 ? "var(--ink-3)"
                        : delta > 0 ? "var(--save-deep)" : "var(--stamp)" }}>
                      {delta === null || delta === 0
                        ? formatCents(b.balanceCents)
                        : `${delta > 0 ? "+" : "\u2212"}${formatCents(Math.abs(delta))}`}
                    </span>
                    <input
                      inputMode="decimal"
                      aria-label={`${b.name} counted amount`}
                      value={counts[b.id] ?? ""}
                      onChange={(e) => { setDirty(true); setCounts({ ...counts, [b.id]: e.target.value }); }}
                      className="money"
                      style={{ width: 96, minHeight: 44, textAlign: "right" }}
                    />
                  </div>
                );
              })}
            </div>
            <Field label="Note (optional)">
              <input
                value={countMemo}
                onChange={(e) => setCountMemo(e.target.value)}
                placeholder="Sunday count"
                maxLength={140}
              />
            </Field>
            <button disabled={busy === "count" || countTotalDelta === null}
                    style={{ minHeight: 48 }}>
              {busy === "count"
                ? "Saving\u2026"
                : countTotalDelta === 0
                  ? "Already correct"
                  : `Set the jars (${countTotalDelta! > 0 ? "+" : "\u2212"}${
                      formatCents(Math.abs(countTotalDelta ?? 0))})`}
            </button>
          </form>
        </Line>

        <Line label="Add or take away money">
          <p style={{ margin: "0 0 12px", fontSize: 13.5, color: "var(--ink-2)" }}>
            A bonus or a deduction. Use a minus sign to take money away. This is
            not split across jars &mdash; pick the one it belongs in.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const cents = Math.round(parseFloat(adjustAmount) * 100);
              if (!Number.isFinite(cents) || cents === 0) {
                setError("Enter an amount that isn't zero.");
                return;
              }
              void run(
                "adjust",
                async () => {
                  await api.post(`/children/${childId}/adjust`, {
                    bucketId: adjustJar,
                    amountCents: cents,
                    memo: adjustMemo.trim(),
                  });
                  setAdjustAmount("");
                  setAdjustMemo("");
                },
                "Done.",
              );
            }}
            style={{ display: "grid", gap: 12 }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Which jar">
                <select value={adjustJar} onChange={(e) => setAdjustJar(e.target.value)}>
                  {buckets.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Amount" hint="e.g. 2.50 or -1.00">
                <input inputMode="decimal" value={adjustAmount}
                       onChange={(e) => setAdjustAmount(e.target.value)} />
              </Field>
            </div>
            <Field label="What's it for?" hint="They'll see this in their history.">
              <input value={adjustMemo} maxLength={140}
                     onChange={(e) => setAdjustMemo(e.target.value)}
                     placeholder="Raked the leaves" />
            </Field>
            <button type="submit" disabled={busy === "adjust"}>
              {busy === "adjust" ? "Saving…" : "Add it"}
            </button>
          </form>
        </Line>

        {buckets.length > 1 && (
          <Line label="Move money between jars">
          <p style={{ margin: "0 0 12px", fontSize: 13.5, color: "var(--ink-2)" }}>
            Nothing is created or destroyed &mdash; it just changes jars.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const cents = Math.round(parseFloat(moveAmount) * 100);
              if (!Number.isFinite(cents) || cents <= 0) {
                setError("Enter an amount to move.");
                return;
              }
              setPendingMove({ fromId: fromJar, toId: toJar, cents });
            }}
            style={{ display: "grid", gap: 12 }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <Field label="From">
                <select value={fromJar} onChange={(e) => setFromJar(e.target.value)}>
                  {buckets.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="To">
                <select value={toJar} onChange={(e) => setToJar(e.target.value)}>
                  {buckets.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Amount">
                <input inputMode="decimal" value={moveAmount}
                       onChange={(e) => setMoveAmount(e.target.value)} />
              </Field>
            </div>
            {pendingMove ? (
              <div role="alertdialog" aria-label="Confirm move"
                   style={{ display: "grid", gap: 10, padding: 14,
                            border: "2px solid var(--ink)", borderRadius: 10 }}>
                <p style={{ margin: 0, fontWeight: 600 }}>
                  Move <span className="money">{formatCents(pendingMove.cents)}</span>{" "}
                  from <strong>{buckets.find((b) => b.id === pendingMove.fromId)?.name}</strong>{" "}
                  to <strong>{buckets.find((b) => b.id === pendingMove.toId)?.name}</strong>?
                </p>
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    type="button"
                    disabled={busy === "transfer"}
                    onClick={() => {
                      const move = pendingMove;
                      setPendingMove(null);
                      void run(
                        "transfer",
                        async () => {
                          await api.post(`/children/${childId}/transfer`, {
                            fromBucketId: move.fromId,
                            toBucketId: move.toId,
                            amountCents: move.cents,
                          });
                          setMoveAmount("");
                        },
                        "Moved.",
                      );
                    }}
                    style={{ flex: 1 }}
                  >
                    Yes, move it
                  </button>
                  <button type="button" onClick={() => setPendingMove(null)}
                          disabled={busy === "transfer"}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button type="submit" disabled={busy === "transfer" || fromJar === toJar}>
                {fromJar === toJar ? "Pick two different jars" : "Review move"}
              </button>
            )}
          </form>
          </Line>
        )}

        <Line
          label="Saving goal"
          value={goal ? goal.name : "none"}
        >
          {goal ? (
            <div style={{ display: "grid", gap: 10 }}>
              <p style={{ margin: 0 }}>
                <strong>{goal.name}</strong> &mdash;{" "}
                <span className="money">{formatCents(goal.savedCents)}</span> of{" "}
                <span className="money">{formatCents(goal.targetCents)}</span>
              </p>
              <button
                disabled={busy === "goal"}
                onClick={() =>
                  run("goal", () => api.del(`/children/${childId}/goals/${goal.id}`),
                      "Goal removed.")
                }
                style={{ justifySelf: "start", minHeight: 44, fontSize: 14 }}
              >
                Remove goal
              </button>
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void run(
                  "goal",
                  async () => {
                    await api.post(`/children/${childId}/goals`, {
                      name: goalName.trim(),
                      targetCents: Math.round(parseFloat(goalTarget) * 100),
                      bucketKey: "spend",
                    });
                    setGoalName("");
                    setGoalTarget("");
                  },
                  "Goal set.",
                );
              }}
              style={{ display: "grid", gap: 12 }}
            >
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
                <Field label="Saving for">
                  <input value={goalName} required maxLength={60}
                         onChange={(e) => setGoalName(e.target.value)}
                         placeholder="LEGO set" />
                </Field>
                <Field label="Costs">
                  <input inputMode="decimal" value={goalTarget} required
                         onChange={(e) => setGoalTarget(e.target.value)} />
                </Field>
              </div>
              <button type="submit" disabled={busy === "goal"}>Set goal</button>
            </form>
          )}
        </Line>
      </Leaf>

      {/* SETUP -- set once and left alone for months, so every row states
           where it stands while closed and a parent can read the lot without
           opening anything. */}
      <Section label="Setup" />
      <Leaf>
        <Line
          label="Allowance"
          value={schedule ? `${formatCents(schedule.amountCents)} · ${FREQUENCIES.find((f) => f.value === frequency)?.label.toLowerCase() ?? ""}` : "not set"}
          alert={!schedule}
          defaultOpen={!schedule}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void run(
                "schedule",
                () =>
                  api.put(`/children/${childId}/schedule`, {
                    frequency,
                    amountCents: Math.round(parseFloat(amount) * 100),
                    weekStartDay: payDay,
                    dayOfMonth,
                    payoutTimeLocal: payTime,
                    minimumCompletionBp: Math.round(parseFloat(threshold) * 100),
                    // "Every other week" has to count from somewhere, or which
                    // week is "on" would drift every time the schedule is saved.
                    ...(frequency === "biweekly"
                      ? { anchorDate: startFrom || today() }
                      : {}),
                    ...(startFrom ? { effectiveFrom: startFrom } : {}),
                  }),
                "Allowance saved.",
              );
            }}
            style={{ display: "grid", gap: 12 }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="How much" hint="Each time it's paid.">
                <input inputMode="decimal" value={amount}
                       onChange={(e) => { setDirty(true); setAmount(e.target.value); }} />
              </Field>
              <Field label="Jobs needed" hint="% of jobs to earn anything.">
                <input inputMode="numeric" value={threshold}
                       onChange={(e) => { setDirty(true); setThreshold(e.target.value); }} />
              </Field>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="How often">
                <select value={frequency} onChange={(e) => { setDirty(true); setFrequency(e.target.value); }}>
                  {FREQUENCIES.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
              </Field>

              {frequency === "monthly" ? (
                <Field label="Pay on">
                  <select value={dayOfMonth}
                          onChange={(e) => { setDirty(true); setDayOfMonth(Number(e.target.value)); }}>
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                      <option key={d} value={d}>{ordinal(d)}</option>
                    ))}
                    {/* The engine clamps per month, so 31 IS "the last day" --
                        February included. */}
                    <option value={31}>Last day of the month</option>
                  </select>
                </Field>
              ) : frequency === "daily" ? (
                <Field label="Pay at">
                  <input type="time" value={payTime}
                         onChange={(e) => { setDirty(true); setPayTime(e.target.value); }} />
                </Field>
              ) : (
                <Field label="Pay on">
                  <select value={payDay} onChange={(e) => { setDirty(true); setPayDay(Number(e.target.value)); }}>
                    {WEEKDAYS.map((d, i) => (
                      <option key={d} value={i}>{d}</option>
                    ))}
                  </select>
                </Field>
              )}
            </div>

            {frequency !== "daily" && (
              <Field label="Pay at">
                <input type="time" value={payTime}
                       onChange={(e) => { setDirty(true); setPayTime(e.target.value); }}
                       style={{ maxWidth: 160 }} />
              </Field>
            )}

            <Field
              label="Start from"
              hint="Leave blank to start today. Put a past date to catch up on weeks you missed — nothing is paid until you approve it below."
            >
              <input type="date" value={startFrom}
                     onChange={(e) => setStartFrom(e.target.value)} />
            </Field>

            <p style={{ margin: 0, fontSize: 13.5, color: "var(--ink-2)" }}>
              {scheduleHint}
            </p>

            <button type="submit" disabled={busy === "schedule"}>
              {busy === "schedule" ? "Saving…" : schedule ? "Update allowance" : "Set allowance"}
            </button>
          </form>

          {preview.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <p style={{ margin: "0 0 8px", fontSize: 13.5, color: "var(--ink-2)" }}>
                {formatCents(Math.round(parseFloat(amount || "0") * 100))} becomes:
              </p>
              <div style={{ display: "grid", gap: 6 }}>
                {preview.map((p) => (
                  <Row key={p.bucketId} color={p.color} label={p.name}
                       meta={p.percentBp != null ? formatBp(p.percentBp) : undefined}
                       amount={p.amountCents} />
                ))}
              </div>
            </div>
          )}
        </Line>

        <Line label="Jars" value={splitSummary ? `${splitSummary}%` : ""}>
          <p style={{ margin: "0 0 12px", fontSize: 13.5, color: "var(--ink-2)" }}>
            How each allowance is divided. Must add up to 100%.
          </p>
          <div style={{ display: "grid", gap: 10 }}>
            {buckets.map((b) => (
              <div key={b.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span aria-hidden="true" style={{
                  width: 12, height: 12, borderRadius: 3,
                  background: b.color ?? "var(--ink-3)", flex: "0 0 auto" }} />
                <span style={{ flex: 1 }}>{b.name}</span>
                <span className="money" style={{ fontSize: 13, color: "var(--ink-3)" }}>
                  {formatCents(b.balanceCents)}
                </span>
                <input
                  inputMode="decimal"
                  aria-label={`${b.name} percentage`}
                  value={percents[b.id] ?? ""}
                  onChange={(e) => { setDirty(true); setPercents({ ...percents, [b.id]: e.target.value }); }}
                  className="money"
                  style={{ width: 78, minHeight: 44, textAlign: "right" }}
                />
                <span style={{ color: "var(--ink-3)" }}>%</span>
              </div>
            ))}
          </div>
          <p
            style={{
              margin: "12px 0",
              fontWeight: 600,
              fontSize: 14,
              color: percentsValid ? "var(--save-deep)" : "var(--bad)",
            }}
          >
            Total: {percentTotal.toFixed(2)}%{percentsValid ? "" : " — must be 100%"}
          </p>
          <button
            disabled={!percentsValid || busy === "buckets"}
            onClick={() =>
              run(
                "buckets",
                () =>
                  api.put(`/children/${childId}/buckets`, {
                    buckets: buckets.map((b) => ({
                      key: b.key,
                      name: b.name,
                      kind: b.kind,
                      percentBp: Math.round(parseFloat(percents[b.id] || "0") * 100),
                      icon: b.icon,
                      color: b.color,
                      withdrawable: b.withdrawable,
                      sortOrder: b.sortOrder,
                      interestEnabled: Boolean(
                        (b.interest as { enabled?: boolean } | null)?.enabled,
                      ),
                      interestRateBp: Number(
                        (b.interest as { rate_bp?: number } | null)?.rate_bp ?? 0,
                      ),
                    })),
                  }),
                "Jars saved.",
              )
            }
          >
            {busy === "buckets" ? "Saving…" : "Save jars"}
          </button>
        </Line>

        {special && (
          <Line label={`The ${special.name} jar`} value={policyLabel.toLowerCase()}>
          <p style={{ margin: "0 0 14px", fontSize: 13.5, color: "var(--ink-2)" }}>
            {child.displayName} can put money in here any time, whatever else
            they can or can&rsquo;t do &mdash; it takes no share of the
            allowance, so everything in it is money they chose to set aside.
            You decide how it comes back out.
          </p>
          {catalogue && (
            <SpecialPolicyPicker
              options={catalogue.specialPolicies}
              value={child.specialPolicy}
              disabled={busy === "specialPolicy"}
              onChange={(key) =>
                run("specialPolicy", () =>
                  api.patch(`/children/${childId}`, { specialPolicy: key }),
                  "Saved.",
                )
              }
            />
          )}
          <p style={{ margin: "12px 0 0", fontSize: 13.5, color: "var(--ink-3)" }}>
            Currently holding{" "}
            <span className="money" style={{ fontWeight: 700 }}>
              {formatCents(special.balanceCents)}
            </span>.
          </p>
          </Line>
        )}

        <Line
          label="How money looks"
          value={child.showCashPictures ? "notes and coins" : "just the amount"}
        >
          <p style={{ margin: "0 0 14px", fontSize: 13.5, color: "var(--ink-2)" }}>
            The jars can draw what {child.displayName} has as the actual notes
            and coins that make it up &mdash; the fewest of each, with the
            figure printed underneath either way. Tapping a jar opens a panel
            for counting the same amount out different ways. Older kids often
            prefer the plain fill level; this is where you change it.
          </p>
          <div role="radiogroup" aria-label="How money looks"
               style={{ display: "grid", gap: 8 }}>
            {[
              { on: true, label: "Notes and coins",
                blurb: "A picture of the money, countable" },
              { on: false, label: "Just the amount",
                blurb: "A fill level and the figure" },
            ].map((option) => {
              const chosen = child.showCashPictures === option.on;
              return (
                <button
                  key={String(option.on)}
                  type="button"
                  role="radio"
                  aria-checked={chosen}
                  disabled={busy === "cashPictures"}
                  onClick={() =>
                    run("cashPictures", () =>
                      api.patch(`/children/${childId}`, {
                        showCashPictures: option.on,
                      }),
                      "Saved.",
                    )
                  }
                  style={{
                    display: "flex", alignItems: "baseline", gap: 10,
                    textAlign: "left", minHeight: 0, padding: "11px 13px",
                    background: chosen ? "var(--save-soft)" : "var(--paper)",
                    borderColor: chosen ? "var(--save)" : "var(--paper-edge)",
                    borderWidth: chosen ? 3 : 2,
                    boxShadow: "none",
                  }}
                >
                  <span style={{ fontWeight: 600, flex: "0 0 auto" }}>
                    {option.label}
                  </span>
                  <span style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
                    {option.blurb}
                  </span>
                </button>
              );
            })}
          </div>
        </Line>

        <Line
          label="Finishing the day"
          value={child.playJingle ? "plays a tune" : "silent"}
        >
          <p style={{ margin: "0 0 14px", fontSize: 13.5, color: "var(--ink-2)" }}>
            When {child.displayName} ticks off the last job of the day, their
            screen plays a short tune &mdash; once a day, and only from their
            own tap, never from something you do on your phone. The
            &ldquo;All done for today!&rdquo; message stays either way, so
            turning this off costs the noise and not the moment.
          </p>
          <div role="radiogroup" aria-label="Finishing the day"
               style={{ display: "grid", gap: 8 }}>
            {[
              { on: true, label: "Play the tune",
                blurb: "A few notes, quietly" },
              { on: false, label: "Keep it silent",
                blurb: "The message on its own" },
            ].map((option) => {
              const chosen = child.playJingle === option.on;
              return (
                <button
                  key={String(option.on)}
                  type="button"
                  role="radio"
                  aria-checked={chosen}
                  disabled={busy === "jingle"}
                  onClick={() =>
                    run("jingle", () =>
                      api.patch(`/children/${childId}`, {
                        playJingle: option.on,
                      }),
                      "Saved.",
                    )
                  }
                  style={{
                    display: "flex", alignItems: "baseline", gap: 10,
                    textAlign: "left", minHeight: 0, padding: "11px 13px",
                    background: chosen ? "var(--save-soft)" : "var(--paper)",
                    borderColor: chosen ? "var(--save)" : "var(--paper-edge)",
                    borderWidth: chosen ? 3 : 2,
                    boxShadow: "none",
                  }}
                >
                  <span style={{ fontWeight: 600, flex: "0 0 auto" }}>
                    {option.label}
                  </span>
                  <span style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
                    {option.blurb}
                  </span>
                </button>
              );
            })}
          </div>
        </Line>

        <Line
          label={`What ${child.displayName} can do`}
          value={capsTotal ? `${capsOn} of ${capsTotal}` : ""}
        >
          <p style={{ margin: "0 0 14px", fontSize: 13.5, color: "var(--ink-2)" }}>
            Nothing here changes on its own. Every switch stays where you put it
            until you move it.
          </p>

          {catalogue && (
            <>
              <PresetPicker
                presets={catalogue.presets}
                value={child.capabilityPreset}
                disabled={busy === "preset"}
                onChange={(key) =>
                  run("preset", () =>
                    api.patch(`/children/${childId}/capabilities`, { preset: key }),
                    "Switched to that group.",
                  )
                }
              />
              <p style={{ margin: "8px 0 18px", fontSize: 12.5, color: "var(--ink-3)" }}>
                Picking a group resets every switch below to that group.
              </p>

              <CapabilityList
                catalogue={catalogue}
                capabilities={child.capabilities}
                presetKey={child.capabilityPreset}
                busyKey={busy}
                onToggle={(key, next) =>
                  run(key, () =>
                    api.patch(`/children/${childId}/capabilities`, {
                      overrides: { [key]: next },
                    }),
                  )
                }
              />
            </>
          )}
        </Line>

        <Line
          label="Sign-in"
          value={child.loginName ?? "none"}
        >
          <p style={{ margin: "0 0 12px", fontSize: 13.5, color: "var(--ink-2)" }}>
            {child.hasLogin ? (
              <>
                They tap their picture and type{" "}
                <strong>four digits</strong>. Signed in as{" "}
                <span className="money">{child.loginName}</span>.
              </>
            ) : (
              <>
                They don&rsquo;t have one yet. You can still track their jars and
                jobs without it &mdash; add one when they&rsquo;re ready.
              </>
            )}
          </p>

          {editingLogin || !child.hasLogin ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void run(
                  "login",
                  async () => {
                    await api.put(`/children/${childId}/login`, {
                      ...(loginName.trim() ? { loginName: loginName.trim() } : {}),
                      pin,
                    });
                    setPin("");
                    setLoginName("");
                    setEditingLogin(false);
                  },
                  child.hasLogin ? "New number saved." : "Sign-in added.",
                );
              }}
              style={{ display: "grid", gap: 12 }}
            >
              <Field
                label="Sign-in name"
                hint={
                  child.hasLogin
                    ? "Leave blank to keep the one they have."
                    : "Lowercase, no spaces."
                }
              >
                <input
                  value={loginName}
                  onChange={(e) => setLoginName(e.target.value)}
                  autoCapitalize="none"
                  maxLength={40}
                  required={!child.hasLogin}
                  /* Their own name, not a hardcoded example -- suggesting
                     another child's name on this form is a small thing that
                     reads as a bug. */
                  placeholder={
                    child.loginName ??
                    child.displayName.toLowerCase().replace(/[^a-z0-9]/g, "")
                  }
                />
              </Field>
              <Field
                label={child.hasLogin ? "New secret number" : "Secret number"}
                hint="Any four digits they can remember on their own."
              >
                <input
                  inputMode="numeric"
                  pattern="\d{4}"
                  maxLength={4}
                  value={pin}
                  required
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  className="money"
                  style={{ fontSize: 24, letterSpacing: "0.4em", textAlign: "center" }}
                />
              </Field>
              <div style={{ display: "flex", gap: 10 }}>
                <button type="submit" disabled={busy === "login" || pin.length !== 4}
                        style={{ flex: 1 }}>
                  {busy === "login"
                    ? "Saving…"
                    : child.hasLogin ? "Save new number" : "Add sign-in"}
                </button>
                {child.hasLogin && (
                  <button type="button" disabled={busy === "login"}
                          onClick={() => { setEditingLogin(false); setPin(""); }}>
                    Cancel
                  </button>
                )}
              </div>
              {child.hasLogin && (
                <p style={{ margin: 0, fontSize: 12.5, color: "var(--ink-3)" }}>
                  Saving a new number signs them out on every device. You
                  don&rsquo;t need to know the old one.
                </p>
              )}
            </form>
          ) : (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button onClick={() => setEditingLogin(true)}>
                Change their number
              </button>
              <button
                disabled={busy === "removeLogin"}
                onClick={() =>
                  run("removeLogin", () => api.del(`/children/${childId}/login`),
                      "Sign-in removed.")
                }
              >
                Remove their sign-in
              </button>
            </div>
          )}
        </Line>
      </Leaf>

      {/* RECORD -- what already happened. Read-only apart from Undo. */}
      <Section label="Record" />
      <Leaf>
        <Line label="Monthly statement" value={month}>
          <input
            type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            style={{ width: "auto", minHeight: 44, marginBottom: 14 }}
            aria-label="Statement month"
          />

          {statement && statement.count > 0 ? (
            <>
              <div style={{
                display: "grid", gridTemplateColumns: "repeat(3, 1fr)",
                gap: 10, marginBottom: 14 }}>
                <Stat label="Money in" cents={statement.moneyInCents} tone="var(--save-deep)" />
                <Stat label="Money out" cents={statement.moneyOutCents} tone="var(--bad)" />
                <Stat label="Change" cents={statement.netCents}
                      tone={statement.netCents >= 0 ? "var(--save-deep)" : "var(--bad)"} />
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{
                  width: "100%", borderCollapse: "collapse",
                  fontFamily: "var(--font-num)", fontSize: 13 }}>
                  <tbody>
                    {statement.rows.map((row) => (
                      <tr key={row.id} style={{ borderBottom: "1px solid var(--paper-edge)" }}>
                        <td style={{ padding: "7px 8px 7px 0", color: "var(--ink-3)", whiteSpace: "nowrap" }}>
                          {row.localDate}
                        </td>
                        <td style={{ padding: 7, fontFamily: "var(--font-ui)" }}>
                          {row.label}{row.memo && (
                            <span style={{ color: "var(--ink-3)" }}> · {row.memo}</span>
                          )}
                        </td>
                        <td className="money" style={{
                          padding: "7px 0 7px 8px", textAlign: "right", fontWeight: 700,
                          color: amountReading(row).tone }}>
                          {amountReading(row).text}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <p style={{ color: "var(--ink-2)", margin: 0 }}>Nothing happened that month.</p>
          )}

          {spending && spending.totalCents > 0 && (
            <div style={{ marginTop: 18 }}>
              <h3 style={{ fontSize: 15, marginBottom: 8 }}>
                What they&rsquo;ve spent cash on, all time
              </h3>
              <div style={{ display: "grid", gap: 6 }}>
                {spending.byCategory.map((c) => (
                  <div key={c.category} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ width: 76, textTransform: "capitalize", fontSize: 14 }}>
                      {c.category}
                    </span>
                    <span style={{
                      flex: 1, height: 12, background: "var(--paper-edge)",
                      borderRadius: 999, overflow: "hidden" }}>
                      <span style={{
                        display: "block", height: "100%", background: "var(--spend)",
                        width: `${(c.amountCents / spending.totalCents) * 100}%` }} />
                    </span>
                    <span className="money" style={{ fontWeight: 700, fontSize: 13 }}>
                      {formatCents(c.amountCents)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Line>

        <Line label="Everything that happened" value={`${history.length} entries`}>
          {history.length === 0 ? (
            <p style={{ color: "var(--ink-2)", margin: 0 }}>Nothing yet.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{
                width: "100%", borderCollapse: "collapse",
                fontFamily: "var(--font-num)", fontSize: 13 }}>
                <tbody>
                  {history.map((row) => (
                    <tr key={row.id} style={{
                      borderBottom: "1px solid var(--paper-edge)",
                      opacity: row.isReversed ? 0.5 : 1 }}>
                      <td style={{ padding: "8px 8px 8px 0", color: "var(--ink-3)", whiteSpace: "nowrap" }}>
                        {row.localDate ?? ""}
                      </td>
                      <td style={{
                        padding: 8, fontFamily: "var(--font-ui)",
                        textDecoration: row.isReversed ? "line-through" : "none" }}>
                        {row.label}
                        {row.memo && <span style={{ color: "var(--ink-3)" }}> · {row.memo}</span>}
                      </td>
                      <td className="money" style={{
                        padding: "8px 0 8px 8px", textAlign: "right", fontWeight: 700,
                        whiteSpace: "nowrap", color: amountReading(row).tone }}>
                        {amountReading(row).text}
                      </td>
                      <td style={{ padding: "4px 0 4px 8px" }}>
                        {!row.isReversed && !row.isReversal && (
                          <button
                            disabled={busy === row.id}
                            onClick={() =>
                              run(row.id, () => api.post(`/transactions/${row.id}/reverse`),
                                  "Undone.")
                            }
                            style={{ minHeight: 36, fontSize: 12, padding: "0 10px", boxShadow: "none" }}
                          >
                            Undo
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Line>
      </Leaf>

      {/* Unboxed and last: the only thing here a parent cannot undo. */}
      <div
        style={{
          marginTop: 34,
          paddingTop: 18,
          borderTop: "2px dashed var(--paper-edge)",
        }}
      >
        <p style={{ margin: "0 0 10px", fontSize: 13.5, color: "var(--ink-2)" }}>
          Removing {child.displayName} hides them and stops their allowance.
          Their history is kept.
        </p>
        {/* The one thing on this page that cannot be undone, so it is the one
            thing that does not look like a button you press by accident. */}
        <button
          disabled={busy === "archive"}
          onClick={() =>
            run("archive", async () => {
              await api.del(`/children/${childId}`);
              onArchived();
            })
          }
          style={{
            minHeight: 44,
            padding: "0 14px",
            fontSize: 14,
            background: "none",
            boxShadow: "none",
            borderColor: "var(--stamp)",
            color: "var(--stamp)",
          }}
        >
          Remove {child.displayName}
        </button>
      </div>

      {note && (
        <p style={{ color: "var(--save-deep)", fontWeight: 600, marginTop: 16 }}>{note}</p>
      )}
      {error && (
        <p role="alert" style={{ color: "var(--bad)", fontWeight: 600, marginTop: 16 }}>
          {error}
        </p>
      )}
    </div>
  );
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/* "07:00" -> "7am". Times a parent reads aloud, not a 24-hour clock. */
function formatTime(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number);
  if (!Number.isFinite(h)) return hhmm;
  const suffix = h < 12 ? "am" : "pm";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return m ? `${hour}:${String(m).padStart(2, "0")}${suffix}` : `${hour}${suffix}`;
}

function ordinal(n: number): string {
  // 31 is how the engine spells "the last day", whatever the month is.
  if (n === 31) return "the last day";
  const tens = n % 100;
  const suffix =
    tens >= 11 && tens <= 13
      ? "th"
      : ["th", "st", "nd", "rd"][n % 10] ?? "th";
  return `the ${n}${suffix}`;
}

/* A legend swatch. The grid is dense enough that four states need naming
   once; leaving a parent to infer "outlined green" is how a claimed job gets
   mistaken for a done one. */
function Key({ look, label }: { look: CellState; label: string }) {
  const style: Record<CellState, React.CSSProperties> = {
    done: { background: "var(--save)", borderColor: "var(--save-deep)", color: "#fff" },
    claimed: { background: "var(--save-soft)", borderColor: "var(--save)", color: "var(--save-deep)" },
    missed: { borderColor: "var(--bad)", color: "var(--bad)" },
    excused: { borderColor: "var(--ink-3)", color: "var(--ink-3)" },
    open: { borderColor: "var(--paper-edge)", color: "var(--ink-3)" },
  };
  const mark = { done: "\u2713", claimed: "\u2713", missed: "\u00d7", excused: "\u2013", open: "" };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span aria-hidden="true" style={{
        width: 18, height: 18, borderRadius: 6, borderWidth: 2, borderStyle: "solid",
        display: "grid", placeItems: "center", fontSize: 11, fontWeight: 700,
        lineHeight: 1, ...style[look] }}>
        {mark[look]}
      </span>
      {label}
    </span>
  );
}


function Stat({ label, cents, tone }: { label: string; cents: number; tone: string }) {
  return (
    <div style={{
      padding: "10px 12px", border: "2px solid var(--paper-edge)",
      borderRadius: "var(--r-md)" }}>
      <span style={{ display: "block", fontSize: 12, color: "var(--ink-3)" }}>{label}</span>
      <span className="money" style={{ fontSize: 17, fontWeight: 700, color: tone }}>
        {formatCents(cents)}
      </span>
    </div>
  );
}

function Row({ color, label, meta, amount }: {
  color: string | null; label: string; meta?: string; amount: number;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span aria-hidden="true" style={{
        width: 12, height: 12, borderRadius: 3,
        background: color ?? "var(--ink-3)", flex: "0 0 auto" }} />
      <span style={{ flex: 1 }}>{label}</span>
      {meta && (
        <span className="money" style={{ fontSize: 13, color: "var(--ink-3)" }}>{meta}</span>
      )}
      <span className="money" style={{ fontWeight: 700 }}>{formatCents(amount)}</span>
    </div>
  );
}